import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html } from 'lit';
import { getServiceNowClients, getServiceNowEngineers } from '../../api/servicenow-lookups.js';
import { type SNClientOption, type SNFieldEngineerOption } from '../../db/SNLookups.js';

/**
 * Form-field binding produced by `useForm().field(...)`. Per
 * `FunctionalFormFieldProps`, **`onPicked` is the channel that actually writes
 * to the document** — `onChanged` fires on every keystroke and must not be used
 * for persistence. We translate at this boundary: hand the dropdown the entity
 * matching the current sys_id (so it renders the human-readable name), and
 * translate the picked entity back to its sys_id before bubbling to the form.
 */
interface FieldBinding {
  value?: string | null;
  onPicked?: (value: string | null) => void;
  onChanged?: (value: string | null) => void;
}

const idOf = <T extends { id?: string | null | undefined }>(picked: T | T[] | null | undefined): string | null =>
  Array.isArray(picked) ? (picked[0]?.id ?? null) : (picked?.id ?? null);

/**
 * Searchable dropdown of ServiceNow field engineers. Wires the SN engineer_list
 * endpoint into a DropdownSelectionInput so admins pick by name and the sys_id
 * is what actually lands in the database.
 */
export const ServiceNowEngineerPicker = stateful((props: FieldBinding & { placeholder?: string }) => {
  const enginsQuery = useQuery(
    () => getServiceNowEngineers()({ id: 1, firstName: 1, lastName: 1, email: 1 }),
    [],
  );

  if (enginsQuery.loading) {
    return html`<span class="loading loading-spinner loading-sm"></span>`;
  }

  const engineers = (enginsQuery.value ?? []) as SNFieldEngineerOption[];
  const sorted = [...engineers].sort((a, b) =>
    `${a.lastName ?? ''} ${a.firstName ?? ''}`.localeCompare(`${b.lastName ?? ''} ${b.firstName ?? ''}`),
  );
  const selected = props.value ? sorted.find(e => e.id === props.value) ?? null : null;

  return DropdownSelectionInput<SNFieldEngineerOption, false>({
    selectables: sorted,
    value: selected,
    // `onPicked` is the persistence channel; `onChanged` is for side-effects only.
    onPicked: picked => props.onPicked?.(idOf(picked as SNFieldEngineerOption | null)),
    onChanged: picked => props.onChanged?.(idOf(picked as SNFieldEngineerOption | null)),
    placeholder: props.placeholder ?? 'Search field engineer…',
    renderString: e => {
      const name = [e.firstName, e.lastName].filter(Boolean).join(' ').trim();
      const labelName = name || e.email || e.id || '?';
      return e.email && name ? `${labelName} · ${e.email}` : labelName;
    },
    resolveID: e => e.id ?? '',
  });
});

/**
 * Searchable dropdown of ServiceNow clients (customers).
 *
 * `showId` appends each client's ServiceNow id to its label, which also makes the id searchable. It tells apart
 * clients that share a name (nsctest has several "DELL…") and is what the partner company form turns on.
 *
 * `parentsOnly` lists only parent (top-level) companies, i.e. those with no `parent_company_id` in ServiceNow. Only the
 * list is narrowed: a company that is already selected keeps showing, even if it is a child.
 */
export const ServiceNowClientPicker = stateful(
  (props: FieldBinding & { placeholder?: string; showId?: boolean; parentsOnly?: boolean }) => {
    const clientsQuery = useQuery(() => getServiceNowClients()({ id: 1, name: 1, code: 1, parentCompanyId: 1 }), []);

    if (clientsQuery.loading) {
      return html`<span class="loading loading-spinner loading-sm"></span>`;
    }

    const clients = (clientsQuery.value ?? []) as SNClientOption[];
    const sorted = [...clients].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    // The selection is looked up in the full list so that an already linked child company still shows.
    const selected = props.value ? sorted.find(c => c.id === props.value) ?? null : null;
    const selectables = props.parentsOnly ? sorted.filter(c => !c.parentCompanyId) : sorted;

    return DropdownSelectionInput<SNClientOption, false>({
      selectables,
      value: selected,
      onPicked: picked => props.onPicked?.(idOf(picked as SNClientOption | null)),
      onChanged: picked => props.onChanged?.(idOf(picked as SNClientOption | null)),
      placeholder: props.placeholder ?? 'Search customer…',
      renderString: c => {
        const label = c.code ? `${c.name} (${c.code})` : (c.name ?? c.id ?? '?');
        return props.showId && c.id ? `${label} · ${c.id}` : label;
      },
      resolveID: c => c.id ?? '',
    });
  },
);
